// App.js (라우팅 수정 - SportsMBTI 추가)
import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import MainNavbar from "./MainNavbar";
import Home from "./pages/Home";
import Community from "./pages/Community";
import CommunityBoard from "./pages/CommunityBoard";
import DirectMessage from "./pages/DirectMessage";
import AddFriend from "./pages/AddFriend";
import Mypage from "./pages/Mypage";
import Ranking from "./pages/Ranking";
import Guild from "./pages/Guild";
import Match from "./pages/Match";
import LoginRegister from "./pages/LoginRegister";
import PostDetail from "./pages/PostDetail";
import NotificationBell from "./pages/NotificationBell";
import Intro from "./pages/Intro";
import SportsMBTI from "./pages/SportsMBTI";

// 길드 관련 컴포넌트들
import GuildMemberManagement from "./pages/GuildMemberManagement";
import GuildIntro from "./pages/GuildIntro";
import GuildEvents from "./pages/GuildEvents";

// 협회/대회 관련 페이지
import AssociationDashboard from "./pages/AssociationDashboard";
import CreateTournament from "./pages/CreateTournament";
import TournamentManagement from "./pages/TournamentManagement";
import TournamentList from "./pages/TournamentList";
import TournamentDetail from "./pages/TournamentDetail";
import TournamentRegistration from "./pages/TournamentRegistration";
import TournamentResults from "./pages/TournamentResults";
import AssociationProfile from "./pages/AssociationProfile";

// 길드 분석 페이지 (필요한 경우)
// import GuildAnalytics from "./pages/GuildAnalytics";

function App() {
  const [user, setUser] = useState(null);
  const [acceptedMatchInfo, setAcceptedMatchInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  // 사용자 상태 초기화 및 관리
  useEffect(() => {
    const initializeUser = () => {
      try {
        const storedUser = localStorage.getItem("user");
        if (storedUser) {
          const parsedUser = JSON.parse(storedUser);
          setUser(parsedUser);
        }
      } catch (error) {
        console.error("사용자 정보 로딩 실패:", error);
        localStorage.removeItem("user"); // 손상된 데이터 제거
      } finally {
        setLoading(false);
      }
    };

    initializeUser();
  }, []);

  // 사용자 상태 업데이트 함수
  const updateUser = (newUserData) => {
    setUser(newUserData);
    if (newUserData) {
      localStorage.setItem("user", JSON.stringify(newUserData));
    } else {
      localStorage.removeItem("user");
    }
  };

  // 사용자 타입 체크
  const isAssociation = user?.userType === 'association';
  const isAuthenticated = !!user;

  // 로딩 중일 때
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: '#fff',
        fontSize: 18,
        fontWeight: 600
      }}>
        🏆 Sportier 로딩 중...
      </div>
    );
  }

  // 인증이 필요한 라우트 컴포넌트
  const ProtectedRoute = ({ children }) => {
    return isAuthenticated ? children : <Navigate to="/login" replace />;
  };

  // 협회 전용 라우트 컴포넌트
  const AssociationRoute = ({ children }) => {
    if (!isAuthenticated) return <Navigate to="/login" replace />;
    if (!isAssociation) return <Navigate to="/" replace />;
    return children;
  };

  // 게스트 전용 라우트 컴포넌트
  const GuestRoute = ({ children }) => {
    return !isAuthenticated ? children : <Navigate to="/" replace />;
  };

  return (
    <Router>
      <MainNavbar user={user} updateUser={updateUser}>
        <NotificationBell 
          user={user}
          setAcceptedMatchInfo={setAcceptedMatchInfo} 
        />
      </MainNavbar>
      
      <Routes>
        {/* 공개 라우트 */}
        <Route path="/" element={<Home />} />
        <Route path="/intro" element={<Intro />} />
        <Route path="/sports-mbti" element={<SportsMBTI />} />
        <Route path="/tournaments" element={<TournamentList />} />
        <Route path="/tournament/:tournamentId" element={<TournamentDetail />} />
        <Route path="/ranking" element={<Ranking />} />
        
        {/* 인증 관련 라우트 */}
        <Route path="/login" element={<GuestRoute><LoginRegister updateUser={updateUser} /></GuestRoute>} />
        <Route path="/register" element={<GuestRoute><LoginRegister updateUser={updateUser} /></GuestRoute>} />
        <Route path="/LoginRegister" element={<LoginRegister />} />
        
        {/* 보호된 라우트 (로그인 필요) */}
        <Route path="/community" element={<ProtectedRoute><Community /></ProtectedRoute>} />
        <Route path="/community/board" element={<ProtectedRoute><CommunityBoard /></ProtectedRoute>} />
        <Route path="/community/post/:postId" element={<ProtectedRoute><PostDetail user={user} /></ProtectedRoute>} />
        <Route path="/community/dm" element={<ProtectedRoute><DirectMessage /></ProtectedRoute>} />
        <Route path="/community/addfriend" element={<ProtectedRoute><DirectMessage /></ProtectedRoute>} />
        
        {/* 마이페이지 */}
        <Route path="/mypage/:userId" element={<ProtectedRoute><Mypage /></ProtectedRoute>} />
        <Route path="/mypage" element={<ProtectedRoute><Mypage /></ProtectedRoute>} />
        
        {/* 길드 관련 */}
        <Route path="/guild" element={<ProtectedRoute><Guild /></ProtectedRoute>} />
        <Route path="/guild/:guildId" element={<ProtectedRoute><Guild /></ProtectedRoute>} />
        <Route path="/guild/intro/:guildId" element={<GuildIntro />} />
        <Route path="/guild/events" element={<ProtectedRoute><GuildEvents /></ProtectedRoute>} />
        <Route path="/guild/:guildId/management" element={<ProtectedRoute><GuildMemberManagement /></ProtectedRoute>} />
        {/* GuildAnalytics가 필요한 경우 주석 해제
        <Route path="/guild/analytics" element={<ProtectedRoute><GuildAnalytics /></ProtectedRoute>} />
        */}
        
        {/* 매치 */}
        <Route 
          path="/match" 
          element={
            <ProtectedRoute>
              <Match 
                acceptedMatchInfo={acceptedMatchInfo} 
                setAcceptedMatchInfo={setAcceptedMatchInfo}
                user={user}
              />
            </ProtectedRoute>
          } 
        />
        
        {/* 대회 참가 관련 (로그인 필요) */}
        <Route path="/tournament/:tournamentId/register" element={<ProtectedRoute><TournamentRegistration /></ProtectedRoute>} />
        <Route path="/tournament/:tournamentId/results" element={<ProtectedRoute><TournamentResults /></ProtectedRoute>} />
        
        {/* 협회 전용 라우트 */}
        <Route path="/association" element={<AssociationRoute><AssociationDashboard /></AssociationRoute>} />
        <Route path="/association/create-tournament" element={<AssociationRoute><CreateTournament /></AssociationRoute>} />
        <Route path="/association/manage-tournament/:tournamentId" element={<AssociationRoute><TournamentManagement /></AssociationRoute>} />
        <Route path="/association/profile" element={<AssociationRoute><AssociationProfile /></AssociationRoute>} />
        
        {/* 404 처리 */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}

export default App;